<template>
  <div class="authBox">
    <div class="authBox-background">
      <div class="authBox-background-container">
        <div class="authBox-background-container-title">
          <left-square-outlined @click="backup" />
          <span class="titleText">{{ $t('AccountInfo.AuthenCompany.companyAuth') }}</span>
          <a-divider></a-divider>
        </div>
        <div class="authBox-background-container-body">
          <a-form ref="formRef"
                  :model="formState"
                  :rules="formRules"
                  :label-col="labelCol"
                  :wrapper-col="wrapperCol">
            <a-form-item :label="$t('AccountInfo.AuthenCompany.companyName')"
                         name="name"
                         required>
              <a-input v-model:value="formState.name"
                       :placeholder="$t('AccountInfo.AuthenCompany.companyNamePlaceholder')" />
            </a-form-item>
            <a-form-item :label="$t('AccountInfo.AuthenCompany.legalPersonName')"
                         name="legal_person"
                         required>
              <a-input v-model:value="formState.legal_person"
                       :placeholder="$t('AccountInfo.AuthenCompany.legalPersonNamePlaceholder')" />
            </a-form-item>
            <a-form-item :label="$t('AccountInfo.AuthenCompany.businessLicense')"
                         name="license"
                         required>
              <div class="uploadStyle">
                <a-upload v-model:file-list="fileList"
                          name="legalPersonAvatar"
                          class="uploader"
                          list-type="picture-card"
                          :show-upload-list="false"
                          :custom-request="(fileItem: FileInfo) => customUpload(fileItem, 'legalPerson')"
                          :before-upload="beforeUpload"
                          @change="(info: FileInfo) => handleChange(info, 'legalPerson')">
                  <a-image v-if="formState.license"
                           :width="'100%'"
                           :height="'100%'"
                           :preview="false"
                           :src="toUrl(formState.license)" />
                  <div v-else>
                    <loading-outlined v-if="legalPersonLoading"></loading-outlined>
                    <plus-outlined v-else></plus-outlined>
                    <div class="ant-upload-text">{{ $t('AccountInfo.AuthenCompany.uploadBusinessLicense') }}</div>
                  </div>
                </a-upload>
                <div class="uploadStyle-text">
                  <span>{{ $t('AccountInfo.AuthenCompany.licenseInstructions') }}</span>
                  <span>{{ $t('AccountInfo.AuthenCompany.licenseInstruction1') }}</span>
                  <span>{{ $t('AccountInfo.AuthenCompany.licenseInstruction2') }}</span>
                  <span>{{ $t('AccountInfo.AuthenCompany.licenseInstruction3') }}</span>
                </div>
              </div>
            </a-form-item>
            <a-form-item :label="$t('AccountInfo.AuthenCompany.brandLogo')"
                         name="logo"
                         required>
              <div class="uploadStyle">
                <a-upload v-model:file-list="fileList"
                          name="logoAvatar"
                          class="uploader"
                          list-type="picture-card"
                          :show-upload-list="false"
                          :custom-request="(fileItem: FileInfo) => customUpload(fileItem, 'logo')"
                          :before-upload="beforeUpload"
                          @change="(info: FileInfo) => handleChange(info, 'logo')">
                  <a-image v-if="formState.logo"
                           :width="'100%'"
                           :height="'100%'"
                           :preview="false"
                           :src="toUrl(formState.logo)" />
                  <div v-else>
                    <loading-outlined v-if="logoLoading"></loading-outlined>
                    <plus-outlined v-else></plus-outlined>
                    <div class="ant-upload-text">{{ $t('AccountInfo.AuthenCompany.uploadLogo') }}</div>
                  </div>
                </a-upload>
                <div class="uploadStyle-text">
                  <span>{{ $t('AccountInfo.AuthenCompany.logoInstructions1') }}</span>
                  <span>{{ $t('AccountInfo.AuthenCompany.logoInstructions2') }}</span>
                </div>
              </div>
            </a-form-item>
            <a-form-item :label="$t('AccountInfo.AuthenCompany.contactName')"
                         name="contact_name"
                         required>
              <a-input v-model:value="formState.contact_name"
                       :placeholder="$t('AccountInfo.AuthenCompany.contactNamePlaceholder')" />
            </a-form-item>
            <a-form-item :label="$t('AccountInfo.AuthenCompany.contactPhone')"
                         name="contact_phone"
                         required>
              <a-input v-model:value="formState.contact_phone"
                       :placeholder="$t('AccountInfo.AuthenCompany.contactPhonePlaceholder')" />
            </a-form-item>
            <a-form-item :label="$t('AccountInfo.AuthenCompany.contactEmail')"
                         name="contact_email"
                         required>
              <a-input v-model:value="formState.contact_email"
                       :placeholder="$t('AccountInfo.AuthenCompany.contactEmailPlaceholder')" />
            </a-form-item>
            <a-form-item :label="$t('AccountInfo.AuthenCompany.hasShop')"
                         name="has_shop"
                         required>
              <a-radio-group v-model:value="formState.has_shop"
                             style="float:left">
                <a-radio :value='true'>{{ $t('AccountInfo.AuthenCompany.yes') }}</a-radio>
                <a-radio :value='false'>{{ $t('AccountInfo.AuthenCompany.no') }}</a-radio>
              </a-radio-group>
            </a-form-item>
            <a-form-item v-if="formState.has_shop == true"
                         :label="$t('AccountInfo.AuthenCompany.shopAddress')"
                         name="shop">
              <a-input v-model:value="formState.shop"
                       :placeholder="$t('AccountInfo.AuthenCompany.shopAddressPlaceholder')" />
            </a-form-item>
            <a-form-item :wrapper-col="{ span: 10, offset: 5 }">
              <a-button type="primary"
                        class="footerSubmit"
                        @click="onSubmit">{{ $t('AccountInfo.AuthenCompany.submit') }}</a-button>
            </a-form-item>
          </a-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CORE_ROUTER } from '@/core'
import { toUrl } from '@/utils'
import { message } from 'ant-design-vue'
import { container } from 'tsyringe'
import { onMounted, reactive, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Router } from 'vue-router'
import { authCompanyEdit, authCompanyRegister, authCompanyState, uploadImage } from '../api/Auth'
import type { CompanyRegisterState, FileInfo, FileItem } from '../api/interface/AuthInterface'

onMounted(() => {
  authCompanyState().then(result => {
    const { status } = result as CompanyRegisterState
    if (status == 'failed') {
      formType.value = true
      for (const resultKey in formState) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (formState as any)[resultKey] = (result as any)[resultKey]
      }
    }
  })
})

// declare State
const router = container.resolve<Router>(CORE_ROUTER)
const formRef = ref()
const labelCol = { span: 8 }
const wrapperCol = { span: 12 }
const formType = ref<boolean>(false)
const { t } = useI18n()
const formState = reactive<CompanyRegisterState>({
  name: '',
  legal_person: '',
  license: '',
  logo: '',
  contact_name: '',
  contact_phone: '',
  contact_email: '',
  has_shop: false,
  shop: ''
})
const formRules = computed(() => ({
  name: [{ type: 'string', required: true, message: t('AccountInfo.AuthenCompany.companyNameError'), trigger: 'blur' }],
  legal_person: [{ type: 'string', required: true, message: t('AccountInfo.AuthenCompany.legalPersonNameError'), trigger: 'blur' }],
  license: [{ type: 'string', required: true, message: t('AccountInfo.AuthenCompany.licenseError'), trigger: 'blur' }],
  logo: [{ type: 'string', required: true, message: t('AccountInfo.AuthenCompany.logoError'), trigger: 'blur' }],
  contact_name: [{ type: 'string', required: true, message: t('AccountInfo.AuthenCompany.contactNameError'), trigger: 'blur' }],
  contact_phone: [{ type: 'string', required: true, trigger: 'blur' }, {
    validator(rule: string, value: string, callback: (...arg: unknown[]) => unknown) {
      if (typeof (value) === 'undefined' || value === '') {
        callback()
      } else {
        if (!testMobilephone(value)) {
          callback(new Error(t('AccountInfo.AuthenCompany.contactPhoneError')))
        }
        callback()
      }
    }
  }],
  contact_email: [{ type: 'email', required: true, message: t('AccountInfo.AuthenCompany.contactEmailError'), trigger: 'blur' }],
  shop: [{ type: 'url', required: true, message: t('AccountInfo.AuthenCompany.shopAddressError'), trigger: 'blur' }],
}))
const fileList = ref([])
const legalPersonLoading = ref<boolean>(false)
const logoLoading = ref<boolean>(false)

// declare Method
const backup = () => {
  router.go(-1)
}

const handleChange = (info: FileInfo, state: 'legalPerson' | 'logo') => {
  if (info.file.status === 'uploading') {
    if (state == 'legalPerson')
      legalPersonLoading.value = true
    else logoLoading.value = true
    return
  }
  if (info.file.status === 'done') {
    if (state == 'legalPerson')
      legalPersonLoading.value = false
    else logoLoading.value = false
  }
  if (info.file.status === 'error') {
    if (state == 'legalPerson')
      legalPersonLoading.value = false
    else logoLoading.value = false
    message.error(t('AccountInfo.AuthenCompany.imageUploadError'))
  }
}

const beforeUpload = (file: FileItem) => {
  const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg'
  if (!isJpgOrPng) {
    message.error(t('AccountInfo.AuthenCompany.imageFormatError'))
  }
  const isLt2M = file.size / 1024 / 1024 < 2
  if (!isLt2M) {
    message.error(t('AccountInfo.AuthenCompany.imageSizeError'))
  }
  return isJpgOrPng && isLt2M
}

const customUpload = (fileItem: { file: FileItem }, state: 'legalPerson' | 'logo') => {
  const { file } = fileItem
  uploadImage(file).then(result => {
    if (result.uploaded) {
      const { uploaded } = result
      if (state == 'legalPerson')
        formState.license = uploaded
      else formState.logo = uploaded
      return true
    }
  })
}

const onSubmit = (): void => {
  if (formState.has_shop == false) {
    formState.shop = ''
  }
  formRef.value.validate().then(() => {
    if (!formType.value) {
      authCompanyRegister(formState).then(() => {
        message.success(t('AccountInfo.AuthenCompany.submitSuccess'))
        backup()
      })
    } else {
      authCompanyEdit(formState).then(() => {
        message.success(t('AccountInfo.AuthenCompany.submitSuccess'))
        backup()
      })
    }
  }).catch(() => {
    //
  })
}

const testMobilephone = (str: string): boolean => {
  const regex = /^1[3456789]\d{9}$/
  if (!regex.test(str)) {
    return false
  } else {
    return true
  }
}

</script>


<style lang="less" scoped>
.authBox {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: rgba(246, 241, 241, 0.24);

  &-background {
    width: 97%;
    height: 95%;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;

    &-container {
      width: 95%;
      justify-content: center;
      text-align: center;
      flex-direction: column;
      overflow: auto;

      &-title {
        text-align: left;
        padding-top: 20px;

        span:first-child {
          font-size: 19px;
        }

        .titleText {
          font-size: 19px;
          padding-left: 13px;
          font-family: PingFangSC-Medium, PingFang SC;
          font-weight: 500;
          color: rgba(0, 0, 0, 0.8500);
          line-height: 24px;
        }
      }

      &-body {
        display: flex;
        justify-content: center;
        align-items: center;

        form {
          width: 80%;

          .uploadStyle {
            display: flex;

            .uploader {
              text-align: left;
              flex-basis: 30%;

              :deep(.ant-upload) {
                width: 21vh;
                height: 15vh;
                overflow: hidden;
              }
            }

            &-text {
              display: flex;
              flex-direction: column;
              font-size: 12px;
              font-family: PingFangSC-Regular, PingFang SC;
              font-weight: 400;
              color: rgba(0, 0, 0, 0.4500);
              line-height: 20px;

              span {
                text-align: left;
                white-space: nowrap;
              }
            }
          }

          .footerSubmit {
            // background: #002FA7
          }
        }
      }
    }
  }
}
</style>
